import React from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PlugsConnected, CheckCircle, CircleNotch } from "@phosphor-icons/react";
import { Card, Field, Input, Btn } from "../epbcs/common";

export default function ConnectionSection() {
  const [form, setForm] = React.useState({ base_url: "", username: "", password: "" });
  const [saved, setSaved] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [testing, setTesting] = React.useState(false);
  const [testResult, setTestResult] = React.useState(null);

  React.useEffect(() => {
    api.get("/reporting/connection").then((r) => {
      setSaved(r.data);
      setForm((f) => ({ ...f, base_url: r.data.base_url || "", username: r.data.username || "" }));
    }).catch(() => setSaved({}));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/reporting/connection", form);
      toast.success("Connection saved");
      const r = await api.get("/reporting/connection");
      setSaved(r.data);
      setForm((f) => ({ ...f, password: "" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save connection");
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await api.post("/reporting/connection/test");
      setTestResult(r.data);
      toast.success(`Connected — ${r.data.root_items} items in catalog root`);
    } catch (e) {
      setTestResult({ ok: false, error: e?.response?.data?.detail || "Connection test failed" });
      toast.error(e?.response?.data?.detail || "Connection test failed");
    } finally { setTesting(false); }
  };

  return (
    <div className="max-w-3xl space-y-4">
      <Card
        title="Oracle Fusion Connection"
        subtitle="Dedicated connection for the Reporting Agent — works across all ERP reports (GL, AP, AR, FA, any BI Publisher catalog). Credentials are encrypted at rest."
        testid="reporting-connection-card"
        actions={saved?.has_password ? (
          <span className="flex items-center gap-1.5 text-[11px] font-bold text-[#1F6B4A]" data-testid="connection-configured-badge">
            <CheckCircle size={14} weight="fill" /> CONFIGURED
          </span>
        ) : null}
      >
        <div className="grid grid-cols-1 gap-3">
          <Field label="Fusion Base URL">
            <Input data-testid="connection-base-url-input"
              placeholder="https://your-pod.fa.ocs.oraclecloud.com"
              value={form.base_url}
              onChange={(e) => setForm((f) => ({ ...f, base_url: e.target.value }))} />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Username">
              <Input data-testid="connection-username-input"
                placeholder="integration.user"
                value={form.username}
                onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))} />
            </Field>
            <Field label={saved?.has_password ? "Password (leave blank to keep current)" : "Password"}>
              <Input data-testid="connection-password-input" type="password"
                placeholder="••••••••"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} />
            </Field>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-5">
          <Btn data-testid="connection-save-btn" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save Connection"}
          </Btn>
          <button data-testid="connection-test-btn" onClick={test} disabled={testing}
            className="flex items-center gap-2 border border-[#0B1E3F] text-[#0B1E3F] px-4 py-2 text-[12px] font-semibold tracking-wide hover:bg-slate-50 transition-colors disabled:opacity-50 rounded-sm">
            {testing ? <CircleNotch size={14} className="animate-spin" /> : <PlugsConnected size={14} weight="bold" />}
            {testing ? "Testing…" : "Test Connection"}
          </button>
        </div>
        {saved?.updated_at && (
          <p className="text-[11px] text-slate-400 mt-3">
            Last updated {new Date(saved.updated_at).toLocaleString()} by {saved.updated_by}
          </p>
        )}
      </Card>

      {testResult && (
        <Card title="Connection Test Result" testid="connection-test-result">
          {testResult.ok ? (
            <div className="text-[13px] text-[#1F6B4A]">
              <p className="font-semibold mb-2">✓ Connected — {testResult.root_items} items visible in the catalog root.</p>
              <div className="flex flex-wrap gap-1.5">
                {(testResult.sample || []).map((n) => (
                  <span key={n} className="text-[11px] bg-slate-100 text-slate-700 px-2 py-0.5 border border-slate-200">{n}</span>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-[13px] text-red-700">{testResult.error}</p>
          )}
        </Card>
      )}
    </div>
  );
}
